import { useCallback, useEffect, useState } from 'react';
import type { CustomerDeploymentStatus } from './api.js';
import { CustomerPolicyEditor } from './customer-policy.js';
import { CustomerAppsEditor } from './customer-apps.js';

export function CustomerPolicyDialog({
  installation,
  initialSurface = 'github',
  onClose,
  onManageInfrastructure,
}: {
  installation: CustomerDeploymentStatus;
  initialSurface?: 'github' | 'apps';
  onClose: () => void;
  onManageInfrastructure: () => void;
}) {
  const [dirty, setDirty] = useState(false);
  const [surface, setSurface] = useState<'github' | 'apps'>(initialSurface);
  const [pendingExit, setPendingExit] = useState<'close' | 'infrastructure' | null>(null);
  const requestExit = useCallback((destination: 'close' | 'infrastructure') => {
    if (dirty) {
      setPendingExit(destination);
      return;
    }
    if (destination === 'close') onClose();
    else onManageInfrastructure();
  }, [dirty, onClose, onManageInfrastructure]);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (pendingExit) setPendingExit(null);
      else requestExit('close');
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [pendingExit, requestExit]);

  const discardAndExit = () => {
    if (pendingExit === 'infrastructure') onManageInfrastructure();
    else onClose();
  };

  return (
    <div
      className="deployment-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestExit('close');
      }}
    >
      <section
        className="policy-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="customer-policy-title"
      >
        <header className="policy-dialog-heading">
          <div>
            <div className="policy-dialog-kicker">
              <span className="policy-github-mark" aria-hidden="true">GH</span>
              <span>Brokered access</span>
              <span className="policy-connected-state"><i /> Broker online</span>
            </div>
            <h2 id="customer-policy-title">Choose what your secure desktops can reach</h2>
            <p>
              Define GitHub and curated-app boundaries. The broker approves matching requests,
              injects customer-owned credentials, and denies everything else.
            </p>
          </div>
          <button type="button" onClick={() => requestExit('close')} aria-label="Close GitHub access">×</button>
        </header>

        <div className="policy-context" aria-label="Connected AWS environment">
          <div>
            <span>AWS account</span>
            <strong>{installation.accountId ?? 'Connected account'}</strong>
          </div>
          <div>
            <span>Private network</span>
            <strong>{installation.vpcId ?? installation.stackName}</strong>
          </div>
          <div>
            <span>Region</span>
            <strong>{installation.region}</strong>
          </div>
          <button type="button" onClick={() => requestExit('infrastructure')}>
            View AWS infrastructure <span aria-hidden="true">→</span>
          </button>
        </div>

        <nav className="policy-surface-tabs" aria-label="Access type">
          <button type="button" aria-pressed={surface === 'github'} onClick={() => setSurface('github')}>
            GitHub repositories
          </button>
          <button type="button" aria-pressed={surface === 'apps'} onClick={() => setSurface('apps')}>
            Curated apps <span>46</span>
          </button>
        </nav>

        {surface === 'github'
          ? <CustomerPolicyEditor installation={installation} onDirtyChange={setDirty} />
          : <CustomerAppsEditor installation={installation} onDirtyChange={setDirty} />}

        {pendingExit && (
          <div className="policy-discard-scrim" role="presentation">
            <section
              className="policy-discard-dialog"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="policy-discard-title"
              aria-describedby="policy-discard-description"
            >
              <span className="policy-discard-mark" aria-hidden="true">!</span>
              <div>
                <span className="eyebrow">Unsaved changes</span>
                <h3 id="policy-discard-title">Leave without saving?</h3>
                <p id="policy-discard-description">
                  Your repository access changes have not been sent to the broker.
                </p>
              </div>
              <footer>
                <button type="button" onClick={() => setPendingExit(null)} autoFocus>Keep editing</button>
                <button type="button" onClick={discardAndExit}>Discard changes</button>
              </footer>
            </section>
          </div>
        )}
      </section>
    </div>
  );
}
